import { useMemo } from 'react';
import { Upload } from '../../../../../services/entities/impl/blfApi/schemas/upload';
import { JsonLabel } from '../../../components/DebugInfoSection';
import { extractAlleyFromFilename } from '../../../utils/extractAlleyFromFilename';
import { formatUploadFileSize } from '../../../utils/formatUploadFileSize';

/**
 * Hook that creates memoized label objects for Upload data
 * Format includes: alley (if available) / size / deviceID / type
 *
 * @param uploads - Array of upload objects
 * @returns Memoized array of JsonLabel objects with id and formatted label
 */
export const useJsonLabelsUploads = (
  uploads: Upload[] | undefined
): JsonLabel[] => {
  return useMemo(() => {
    if (!uploads || !uploads.length) {
      return [];
    }

    // Map uploads to JsonLabel objects
    return uploads
      .filter((upload) => !!upload.id) // Filter out items without ID
      .map((upload) => ({
        id: upload.id,
        label: formatUploadLabel(upload),
      }));
  }, [uploads]);
};

/**
 * Formats an upload label with alley (if available), size, deviceID, and type
 */
const formatUploadLabel = (upload: Upload): string => {
  const parts = [];

  // Try to extract alley from filename (new first position)
  const alley = extractAlleyFromFilename(upload.fileName);
  if (alley) {
    parts.push(alley);
  }

  // File size (second position)
  const formattedSize = formatUploadFileSize(upload.size);
  if (formattedSize !== 'Unknown') {
    parts.push(formattedSize);
  }

  // Device ID (third position)
  if (upload.deviceID) {
    parts.push(upload.deviceID);
  }

  // Type (remains last)
  if (upload.type) {
    parts.push(upload.type);
  }

  // If no parts were added, use a fallback
  if (parts.length === 0 && upload.fileName) {
    return upload.fileName;
  }

  return parts.join(' / ');
};
